import { ArrowBackIcon } from "@chakra-ui/icons";
import { Button, Center, HStack, Link, Text, VStack } from "@chakra-ui/react";
import { signOut, useIsLoggedIn } from "features/auth/AuthUtils";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ErrorPageProps } from "shared/components/empty/ErrorPageProps";
import { InternalErrorDetails } from "shared/components/internal/InternalErrorDetails";
import { RouteLink } from "shared/components/link/RouteLink";
import { useHelpButtonOnClick } from "shared/hooks/useHelpButtonOnClick";
import { HomeIcon } from "shared/icons/HomeIcon";
import { getApiError } from "shared/utils/ErrorUtils";
import { canGoBack } from "shared/utils/NavUtils";

export interface ErrorViewProps extends ErrorPageProps {
  title: string;
}

export const ErrorPageView: React.FC<ErrorViewProps> = ({
  title,
  showSignOut,
  error,
  reset,
}) => {
  const { t } = useTranslation(["common", "auth"]);
  const isLoggedIn = useIsLoggedIn();
  const navigate = useNavigate();
  const message = getApiError(error)?.message;
  const onClickHelp = useHelpButtonOnClick();
  return (
    <Center w="100%" h="100%" data-ls-enabled={true} data-public={true}>
      <VStack
        alignItems="felx-start"
        spacing="48px"
        w="100%"
        maxW="1100px"
        mx="130px"
      >
        <VStack alignItems="flex-start" spacing="16px">
          <Text fontSize="48px" color="gray.700">
            {title}
          </Text>
          {message ? (
            <Text fontSize="16px" color="gray.500">
              {message}
            </Text>
          ) : null}
          <Text fontSize="16px" color="gray.500">
            <Trans ns="common" i18nKey="error.ifPersistsContactSupport">
              If you need help
              <Link colorScheme="gray" onClick={onClickHelp}>
                contact support
              </Link>
              .
            </Trans>
          </Text>
          <InternalErrorDetails error={error} />
        </VStack>
        <HStack>
          {!showSignOut && canGoBack ? (
            <Button
              size="sm"
              colorScheme="tertiary"
              leftIcon={<ArrowBackIcon w="20px" h="20px" />}
              onClick={() => navigate(-1)}
            >
              {t("common:goBack")}
            </Button>
          ) : null}
          {!showSignOut && !canGoBack ? (
            <Button
              as={RouteLink}
              to="/"
              size="sm"
              leftIcon={<HomeIcon w="20px" h="20px" />}
            >
              {t("common:goHome")}
            </Button>
          ) : null}
          {reset && (
            <Button colorScheme="secondary" size="sm" onClick={reset}>
              {t("common:tryAgain")}
            </Button>
          )}
          {showSignOut && isLoggedIn && (
            <Button variant="ghost" size="sm" onClick={() => signOut(true)}>
              {t("auth:signOut")}
            </Button>
          )}
        </HStack>
      </VStack>
    </Center>
  );
};
